import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { SystemStats } from '../types';
import { systemApi, handleApiError } from '../utils/api';
import { formatNumber } from '../utils/helpers';
import { StatsCard } from '../components/StatsCard';
import { Chart } from '../components/Chart';

export function Dashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    // Set up polling for real-time updates
    const interval = setInterval(loadStats, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Memoize session distribution data
  const sessionChartData = React.useMemo(() => ({
    labels: ['Active', 'Pending', 'Completed', 'Expired'],
    datasets: [{
      data: [
        stats?.active_sessions || 0,
        stats?.pending_sessions || 0,
        stats?.completed_sessions || 0,
        (stats?.total_sessions || 0) - (stats?.active_sessions || 0) - (stats?.pending_sessions || 0) - (stats?.completed_sessions || 0)
      ],
      backgroundColor: [
        'rgb(34, 197, 94)',
        'rgb(250, 204, 21)',
        'rgb(59, 130, 246)',
        'rgb(239, 68, 68)',
      ]
    }]
  }), [stats]);

  const loadStats = async () => {
    try {
      const response = await systemApi.getStats();
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow loading-shimmer h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadStats}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const sessionsUtilization = (stats.active_sessions / (stats.total_sessions || 1)) * 100;
  const completedPercentage = stats.total_sessions > 0 
    ? ((stats.completed_sessions / stats.total_sessions) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Real-time overview of your Telegram session management system
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={loadStats}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ChartBarIcon className="h-4 w-4 mr-2" />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Key Metrics - 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Sessions"
          value={formatNumber(stats.total_sessions)}
          change={`${stats.active_sessions} active`}
          changeType="neutral"
          icon={ChatBubbleLeftRightIcon}
        />
        <StatsCard
          title="Active Sessions"
          value={formatNumber(stats.active_sessions)}
          change={`${stats.pending_sessions} pending`}
          changeType="positive"
          icon={ChatBubbleLeftRightIcon}
        />
        <StatsCard
          title="Completed Sessions"
          value={formatNumber(stats.completed_sessions)}
          change={`${completedPercentage}% of total`}
          changeType="neutral"
          icon={CheckCircleIcon}
        />
        <StatsCard
          title="Messages Today"
          value={formatNumber(stats.messages_today)}
          change={`${formatNumber(stats.total_messages)} total`}
          changeType="positive"
          icon={EnvelopeIcon}
        />
      </div>

      {/* Stats Grid - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Activity Progress */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Session Activity</h3>
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-500" />
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Active Sessions</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.active_sessions} / {stats.total_sessions}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(sessionsUtilization, 100)}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {sessionsUtilization.toFixed(1)}% of sessions are active
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.active_sessions}</div>
                <div className="text-xs text-gray-500 mt-1">Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.pending_sessions}</div>
                <div className="text-xs text-gray-500 mt-1">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.completed_sessions}</div>
                <div className="text-xs text-gray-500 mt-1">Completed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Overview */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Messages Overview</h3>
            <EnvelopeIcon className="h-5 w-5 text-green-500" />
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Today</span>
                <span className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.messages_today)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${stats.total_messages > 0 
                      ? Math.min((stats.messages_today / stats.total_messages) * 100, 100) 
                      : 0}%` 
                  }}
                />
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Total Messages</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(stats.total_messages)}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-sm text-gray-600">Avg per Session</div>
                <div className="text-xl font-bold text-blue-600 mt-1">
                  {stats.total_sessions > 0 
                    ? (stats.total_messages / stats.total_sessions).toFixed(1)
                    : '0'}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-sm text-gray-600">Today's Rate</div>
                <div className="text-xl font-bold text-green-600 mt-1">
                  {stats.total_messages > 0 
                    ? ((stats.messages_today / stats.total_messages) * 100).toFixed(1)
                    : '0'}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Session Status Distribution</h3>
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="chart-container" style={{ height: '300px' }}>
            <Chart
              key="session-distribution-chart"
              type="doughnut"
              data={sessionChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 15,
                      usePointStyle: true,
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Quick Stats Card */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Quick Statistics</h3>
            <ClockIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Total Sessions</p>
                  <p className="text-xs text-gray-500">All time</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_sessions)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Completed</p>
                  <p className="text-xs text-gray-500">Successfully finished</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.completed_sessions)}</p>
                <p className="text-xs text-gray-500">{completedPercentage}%</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <ClockIcon className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Pending</p>
                  <p className="text-xs text-gray-500">Awaiting activation</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.pending_sessions)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <EnvelopeIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Total Messages</p>
                  <p className="text-xs text-gray-500">All time</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_messages)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
